/**
 * Duration of the tallest layout animation this has to cover.
 *
 * antd closes an Alert by transitioning `max-height` from the node's height to
 * 0 (`antd/es/alert/Alert.js` — `onLeaveStart` / `onLeaveEnd`), over
 * `motionDurationSlow`, which is 0.3s in antd 5's default token set and is not
 * overridden by any brand theme in `theme/`. Rounded up to leave a frame of
 * slack at the end of the transition.
 */
const LAYOUT_SETTLE_MS = 320;

/**
 * Announces that this component just changed the page's height, so anything
 * measuring the layout re-measures.
 *
 * The consumer today is the dashboard onboarding tour's spotlight. rc-tour
 * measures its target on step change and on `window` `resize` ONLY
 * (`@rc-component/tour/es/hooks/useTarget.js` — `updatePos` is a stable
 * `useEvent` subscribed to `resize`, and its effect re-runs only when the target
 * element or `open` changes). Nothing watches the page, so a banner appearing
 * or disappearing under a stationary tour used to leave the mask hole stranded
 * over whatever drifted into it. A React re-render does NOT help: it re-resolves
 * the target element but never re-measures its rect. A synthetic `resize` is the
 * only lever the engine exposes.
 *
 * Why it is the SHIFTER that calls this, rather than the tour watching the page:
 * the alternative needs a `ResizeObserver` on the app chrome plus a
 * `MutationObserver` on the content column, i.e. real machinery in the one
 * consumer, and a stable id on a container that has only a hashed CSS-module
 * class. The cost of this direction is that it relies on cooperation — see the
 * caller list below and add to it.
 *
 * Callers (all global chrome that mounts/unmounts in normal flow):
 *  - `components/ToastAlert/global-toast-alert.tsx` — shifts EVERYTHING,
 *    sidebar included, because it renders in the sticky container above the
 *    whole layout. Auto-hides after 6s, so it shifts twice per toast.
 *  - `components/CriticalNotification/index.tsx` — notification list and the
 *    PII banner it early-returns in place of; both shift the content column.
 *  - `components/CarouselBanners/index.tsx` — shifts the content column.
 *
 * Dispatching once is not enough. The close is ANIMATED, so the page slides for
 * the length of the transition; a single event either measures mid-flight or
 * snaps at the end. Firing once per frame across a bounded window tracks the
 * movement instead — the same approach the tour already uses for antd's Drawer
 * transition (`pages/dashboard/tour/useDashboardTour.ts`). It is cheap:
 * rc-tour's `updatePos` diffs its own stored rect behind a `JSON.stringify`
 * compare, so frames where nothing actually moved cost one measurement and no
 * re-render.
 *
 * @param durationMs how long to keep re-announcing; defaults to the antd Alert
 *   close transition. Pass 0 for an instant, non-animated change.
 * @returns a cancel function — call it from an effect cleanup so a rapid second
 *   change does not leave two loops running.
 */
function announceLayoutShift(durationMs = LAYOUT_SETTLE_MS) {
  let frame = 0;
  /* `performance.now` rather than `Date.now`: this is a frame deadline, and it
     is immune to the wall clock being adjusted mid-animation. */
  const deadline = performance.now() + durationMs;

  const announce = () => {
    window.dispatchEvent(new Event('resize'));
    if (performance.now() >= deadline) {
      frame = 0;
      return;
    }
    frame = window.requestAnimationFrame(announce);
  };

  /* Deferred by a frame so the DOM change that prompted this has been committed
     and laid out. React effects run before the browser paints, and an antd
     Alert's unmount lands on the commit AFTER its `afterClose` fires, so
     measuring synchronously would read the pre-change layout. */
  frame = window.requestAnimationFrame(announce);

  return () => {
    if (frame) window.cancelAnimationFrame(frame);
    frame = 0;
  };
}

export default announceLayoutShift;
